const selectors = {
  container: '.js-collapsible',
  toggle: '[data-collapsible-toggle]',
  content: '[data-collapsible-content]',
  altTextElement: '[data-collapsible-alt-text]',
};

export default class Collapsible {
  private static readonly EXPANDED_CLASS = '-expanded';
  private static readonly COLLAPSING_CLASS = '-collapsing';
  private static readonly DEFAULT_ANIMATION_DURATION = 200; // 400ms is jquery default

  private readonly toggleElement: JQuery;
  private readonly content: JQuery;
  private readonly altTextElement: JQuery;
  private readonly duration: number = 400;
  private readonly collapseSiblings: boolean;
  private readonly collapseOnBlur: boolean;
  private transitioning = false;

  constructor(private container: JQuery) {
    this.toggleElement = container.children().filter(selectors.toggle).first();
    this.content = container.children().filter(selectors.content).first();
    this.altTextElement = container.find(selectors.altTextElement);
    this.duration = parseInt(container.data('collapsible-duration'), 10) || Collapsible.DEFAULT_ANIMATION_DURATION;
    this.collapseSiblings = container.data('collapse-siblings') !== undefined;
    this.collapseOnBlur = container.data('collapse-on-blur') !== undefined;

    if (isNaN(this.duration)) { // eslint-disable-line no-restricted-globals
      this.duration = Collapsible.DEFAULT_ANIMATION_DURATION;
    }
    this.bindCollapseContentOnToggleClick();
    this.bindCloseOnBlur();
    this.container.data('collapsible-instance', this);
  }

  static init(element: JQuery): void {
    element.find(selectors.container).map((i, element) => new Collapsible($(element)));
  }

  static initAll(initElement: JQuery = $('body')): void {
    this.init(initElement);
  }

  static getInstance(collapsible: JQuery | HTMLElement): Collapsible {
    if (collapsible instanceof HTMLElement) {
      collapsible = $(collapsible); // eslint-disable-line no-param-reassign
    }
    return collapsible.data('collapsible-instance') as Collapsible;
  }

  private bindCollapseContentOnToggleClick(): void {
    this.toggleElement.click((event) => {
      if ((event.target).tagName === 'A') {
        return;
      }
      this.toggle();
    });
  }

  private bindCloseOnBlur(): void {
    if (!this.collapseOnBlur) {
      return;
    }

    document.addEventListener('click', (event) => {
      if (this.isExpanded() && !this.container[0].contains(event.target as Node)) {
        this.collapse();
      }
    });
  }

  isExpanded(): boolean {
    return this.container.hasClass(Collapsible.EXPANDED_CLASS);
  }

  getToggle(): JQuery {
    return this.toggleElement;
  }

  getContent(): JQuery {
    return this.content;
  }

  toggle(): void {
    if (this.isExpanded()) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand(): void {
    if (this.transitioning) return;

    if (this.collapseSiblings) this.collapseExpandedSiblings();

    this.transitioning = true;
    this.content.slideDown(this.duration, () => {
      this.transitioning = false;
    });
    this.container.addClass(Collapsible.EXPANDED_CLASS);
    this.container.attr('aria-expanded', 'true');
    this.switchAltText();
  }

  collapseExpandedSiblings(): void {
    this.container.parent().find(`.${Collapsible.EXPANDED_CLASS}`).each((i, el) => {
      const expandedCollapsible = Collapsible.getInstance($(el));
      expandedCollapsible.collapse();
    });
  }

  collapse(): void {
    if (this.transitioning) return;

    this.transitioning = true;
    this.container.addClass(Collapsible.COLLAPSING_CLASS);
    this.container.removeClass(Collapsible.EXPANDED_CLASS);
    this.container.attr('aria-expanded', 'false');
    this.content.slideUp(this.duration, () => {
      this.transitioning = false;
      this.container.removeClass(Collapsible.COLLAPSING_CLASS);
    });
    this.switchAltText();
  }

  private switchAltText(): void {
    if (this.altTextElement.length) {
      const currentText = this.altTextElement.text();
      this.altTextElement.text(this.altTextElement.data('collapsible-alt-text'));
      this.altTextElement.data('collapsible-alt-text', currentText);
    }
  }
}
